/**
 * Tiny fetch wrappers for the Phase 1 auth + quota endpoints. All requests
 * carry cookies (the device_id and JWT cookies are httpOnly, so they're
 * driven by the browser; we just need `credentials: "include"`).
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function _post(path, body) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  let json = null;
  try { json = await r.json(); } catch { /* not JSON */ }
  return { ok: r.ok, status: r.status, body: json };
}

async function _get(path) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    credentials: "include",
  });
  let json = null;
  try { json = await r.json(); } catch { /* not JSON */ }
  return { ok: r.ok, status: r.status, body: json };
}

async function _patch(path, body) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  let json = null;
  try { json = await r.json(); } catch { /* not JSON */ }
  return { ok: r.ok, status: r.status, body: json };
}

async function _delete(path) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
  });
  let json = null;
  try { json = await r.json(); } catch { /* not JSON */ }
  return { ok: r.ok, status: r.status, body: json };
}

export const apiBase = API_BASE;

export const getMe          = ()             => _get("/api/me");
export const patchMe        = (data)         => _patch("/api/me", data);
export const deleteMe       = ()             => _delete("/api/me");
export const postEmailStart = (email)        => _post("/api/auth/email/start",  { email });
export const postEmailVerify= (token)        => _post("/api/auth/email/verify", { token });
export const postLogout     = ()             => _post("/api/auth/logout");
export const postOnboarding = (data)         => _post("/api/onboarding", data);

// Phase 2 — journal
export const listJournal = ({ q, favorited, page = 1, page_size = 20 } = {}) => {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  if (favorited) qs.set("favorited", "true");
  qs.set("page", String(page));
  qs.set("page_size", String(page_size));
  return _get(`/api/journal?${qs.toString()}`);
};
export const getJournalEntry  = (id)        => _get(`/api/journal/${id}`);
export const patchJournal     = (id, data)  => _patch(`/api/journal/${id}`, data);
export const deleteJournal    = (id)        => _delete(`/api/journal/${id}`);

// Phase 2 — memory
export const getMemory   = ()        => _get("/api/memory");
export const patchMemory = (data)    => _patch("/api/memory", data);
export const deleteMemory= ()        => _delete("/api/memory");

// Phase 2 — call-end hook
export const postFinalize = (sessionId, transcript, secondsUsed, engine) =>
  _post("/api/conversations/finalize", {
    session_id: sessionId,
    transcript: transcript || [],
    seconds_used: secondsUsed || 0,
    engine: engine || null,
  });

export const postUsage      = (sessionId, seconds, engine, checkpointId) =>
  _post("/api/voice/usage", {
    session_id: sessionId,
    seconds: Math.max(0, Math.round(seconds)),
    engine: engine || null,
    checkpoint_id: checkpointId || null,
  });

/** Backend returns `{detail: {error, scope, limit_seconds, used_seconds, seconds_remaining}}` on 402. */
export function parseQuotaError(payload) {
  if (!payload) return null;
  const d = payload.detail || payload;
  if (!d || typeof d !== "object" || !d.error) return null;
  if (d.error !== "trial_exhausted" && d.error !== "daily_quota_exhausted") return null;
  return {
    error: d.error,
    scope: d.scope || (d.error === "trial_exhausted" ? "anon" : "user"),
    limit_seconds: Number(d.limit_seconds || 0),
    used_seconds: Number(d.used_seconds || 0),
    seconds_remaining: Number(d.seconds_remaining || 0),
  };
}
